import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import BugReportForm from './BugReportForm';
import FeatureIdeaForm from './FeatureIdeaForm';
import GeneralFeedbackForm from './GeneralFeedbackForm';
import { submitFeedback } from '../../services/feedbackService';

/**
 * Feedback Modal
 * Main container for feedback submission with dynamic forms based on type
 */
const FeedbackModal = ({ open, onClose }) => {
  const [selectedType, setSelectedType] = useState('feedback');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [ticketNumber, setTicketNumber] = useState(null);

  const handleTypeChange = (event, newValue) => {
    setSelectedType(newValue);
    // Reset states when switching types
    setSubmitSuccess(false);
    setSubmitError(null);
    setTicketNumber(null);
  };

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await submitFeedback({
        type: selectedType,
        ...formData
      });

      setSubmitSuccess(true);
      setTicketNumber(response.ticket.ticket_number);

      // Auto-close after 3 seconds
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitError(
        error.message || 'Failed to submit feedback. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset all states
    setSelectedType('feedback');
    setIsSubmitting(false);
    setSubmitSuccess(false);
    setSubmitError(null);
    setTicketNumber(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <span style={{ fontWeight: 600, fontSize: '1.25rem' }}>
          We'd Love Your Feedback!
        </span>
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="close"
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Success message */}
        {submitSuccess && (
          <Alert
            icon={<CheckCircleIcon fontSize="inherit" />}
            severity="success"
            sx={{ mb: 2 }}
          >
            <strong>Thank you! Your feedback has been submitted.</strong>
            {ticketNumber && (
              <div style={{ marginTop: '8px' }}>
                Ticket #{ticketNumber}
              </div>
            )}
            <div style={{ fontSize: '0.875rem', marginTop: '4px' }}>
              This window will close automatically...
            </div>
          </Alert>
        )}

        {/* Error message */}
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        {/* Only show form if not submitted */}
        {!submitSuccess && (
          <>
            {/* Type selector tabs */}
            <Tabs
              value={selectedType}
              onChange={handleTypeChange}
              variant="fullWidth"
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                label="General Feedback"
                value="feedback"
                icon={<FeedbackIcon />}
                iconPosition="start"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}
              />
              <Tab
                label="Bug Report"
                value="bug"
                icon={<BugReportIcon />}
                iconPosition="start"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}
              />
              <Tab
                label="Feature Idea"
                value="feature"
                icon={<LightbulbIcon />}
                iconPosition="start"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}
              />
            </Tabs>

            {/* Dynamic form based on selected type */}
            <Box sx={{ position: 'relative', minHeight: 400 }}>
              {isSubmitting && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 1
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              {selectedType === 'feedback' && (
                <GeneralFeedbackForm
                  onSubmit={handleSubmit}
                  onCancel={handleClose}
                  isSubmitting={isSubmitting}
                />
              )}

              {selectedType === 'bug' && (
                <BugReportForm
                  onSubmit={handleSubmit}
                  onCancel={handleClose}
                  isSubmitting={isSubmitting}
                />
              )}

              {selectedType === 'feature' && (
                <FeatureIdeaForm
                  onSubmit={handleSubmit}
                  onCancel={handleClose}
                  isSubmitting={isSubmitting}
                />
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;

